import { module } from 'modujs';
import { html } from '../utils/environment';
import { gsap } from 'gsap';

const CLASS = {
	OPEN: 'has-player-open',
	PLAYING: 'has-player-playing',
	LOADING: 'has-player-loading',
	MUTED: 'has-player-muted'
}

export default class extends module {
    constructor(m) {
        super(m);

		this.events = {
			click: {
				video: 'clickVideo',
				closeButton: 'clickCloseButton',
				toggleButton: 'toggle',
				soundButton: 'toggleSound'
			},
			mouseenter: {
				controls: 'controlsMouseenter'
			},
			mouseleave: {
				controls: 'controlsMouseleave'
			},
			input: {
				timeline: 'timelineChange'
			},
			change: {
				timeline: 'timelineChangeRelease'
			}
		}

		this.available = true;
		this.isPlaying = true;

		this.$video = this.$('video')[0];
		this.$fpsValue = this.$('fpsValue')[0];
		this.$timeline = this.$('timeline')[0];
		this.$thumbnails = this.$('thumbnails')[0];
		
		this.$videoClone = this.$('videoClone')[0];

		// Default options
		this.muteSound();
    }

    init() {
		this.progress = 0;
    }

	controlsMouseenter(e) {
		if(window.isMobile) return;

		if(!this.available) return;

		e.stopPropagation();
		this.call('hide',{},'Cursor','player');
	}

	controlsMouseleave(e) {
		if(window.isMobile) return;

		if(!this.available) return;

		e.stopPropagation();
		this.call('display',{},'Cursor','player');
	}

	toggle() {
		if(this.$video.paused) {
			this.play();
		} else {
			this.pause();
		}
	}

	// 
	// PLAY / PAUSE
	// 
	play() {
		this.$video.play();
		html.classList.add(CLASS.PLAYING);
	}

	pause() {
		this.$video.pause();
		html.classList.remove(CLASS.PLAYING);
	}

	toggleSound() {
		if(this.$video.muted) {
			this.playSound();
		} else {
			this.muteSound();
		}
	}

	// 
	// TOGGLE SOUND
	// 
	playSound() {
		this.$video.muted = false;
		html.classList.add(CLASS.MUTED);
	}

	muteSound() {
		this.$video.muted = true;
		html.classList.remove(CLASS.MUTED);
	}

	// 
	// OPENING
	// 
	open(param) {
		let src = param.src;
		this.thumbnails = param.thumbnails.split(',');

		if(!this.available) return;

		this.available = false;

		this.$clickedEl = param.clickedEl;

		this.parentBCR = this.$clickedEl.getBoundingClientRect();
		let elBCR = this.el.getBoundingClientRect();

		let offset = 0;
		
		// Set positions
		gsap.set(this.el,{
			y: this.parentBCR.y - offset,
			alpha: 0,
			scaleX: this.parentBCR.width / elBCR.width,
			scaleY: this.parentBCR.height / window.innerHeight,
			borderRadius: 12
		});

		gsap.set(this.$video,{
			y: -this.parentBCR.y,
			scaleX: elBCR.width / this.parentBCR.width,
			scaleY: window.innerHeight / this.parentBCR.height
		});
		
		this.$video.src = src;
		html.classList.add(CLASS.LOADING);
		this.play();

		this.bindLoaded = this.videoLoadedCallback.bind(this);
		this.$video.addEventListener('canplaythrough',this.bindLoaded);
		
		// Manage clone to generate timeline
		this.generateTimeline();

		this.playSound();
	}

	videoLoadedCallback() {
		// Get FPS
		this.getFPS();
				
		this.$video.removeEventListener('canplaythrough',this.bindLoaded);
		html.classList.add(CLASS.OPEN);
		html.classList.remove(CLASS.LOADING);

		let duration = gsap.utils.clamp(0.3, 0.8, Math.abs(this.parentBCR.y)/100 * 0.2);

		let tl = new gsap.timeline();
		tl.to(this.el,{
			alpha: 1,
			duration: 0.3,
			ease: "power3.inOut"
		});
		tl.to(this.el,{
			y: 0,
			scale: 1,
			borderRadius: 0,
			duration,
			delay: 0,
			ease: "power3.inOut",
			onComplete:() => {
				this.openCallback();
			}
		});
		tl.to(this.$video, {
			y: 0,
			scale: 1,
			duration,
			ease: "power3.inOut",
			delay: -duration
		});
	}

	openCallback() {
		this.call('display',{},'Cursor','player');
		this.available = true;

		this.ticker();
	}

	// 
	// CLOSING
	// 

	clickVideo() {		
		if(!window.isMobile) {
			this.close();
		} else {
			this.toggle();
		}
	}

	clickCloseButton() {
		if(window.isMobile) {
			this.close();
		}
	}

	close() {
		if(!this.available) return;
		this.available = false;

		html.classList.remove(CLASS.OPEN);
		this.call('hide',{},'Cursor','player');
		
		let tl = new gsap.timeline();

		this.parentBCR = this.$clickedEl.getBoundingClientRect();
		let elBCR = this.el.getBoundingClientRect();

		let duration = gsap.utils.clamp(0.3, 0.8, Math.abs(this.parentBCR.y)/100 * 0.2);

		let offset = 0;

		// Set positions
		tl.to(this.el,{
			y: this.parentBCR.y - offset,
			scaleX: this.parentBCR.width / elBCR.width,
			scaleY: this.parentBCR.height / window.innerHeight,
			borderRadius: 12,
			duration,
			ease: "power2.inOut"
		});
		tl.to(this.$video, {
			y: -this.parentBCR.y + offset,
			scaleX: elBCR.width / this.parentBCR.width,
			scaleY: window.innerHeight / this.parentBCR.height,
			duration,
			delay: -duration,
			ease: "power2.inOut"
		});
		tl.to(this.el,{
			alpha: 0,
			duration: 0.3,
			ease: "power2.inOut",
			onComplete: () => {
				this.closeCallback();
			}
		});
		
		
	}

	closeCallback() {
		this.pause();
		this.$video.src = '';
		this.available = true;
		
		gsap.set(this.el,{
			y: 0,
			scaleX: 1,
			scaleY: 1
		});
		gsap.set(this.$video,{
			y: 0,
			scaleX: 1,
			scaleY: 1
		});

		this.$video.src = '';

		window.cancelAnimationFrame(this.tickerRAf);
	}

	// 
	// TICKER
	// 
	ticker() {

		if(!this.$video.paused) {
			// / 100
			let progress = (this.$video.currentTime / this.$video.duration) * 100;

			if(this.progress != progress) {
				this.$timeline.value = progress;
				this.updateTimeValue();
				this.el.style.setProperty('--videoProgress',progress);
				this.progress = progress;
			}
			html.classList.add(CLASS.PLAYING);
		} else {
			html.classList.remove(CLASS.PLAYING);

		}

		if(this.$video.currentTime / this.$video.duration == 1) {
			this.close();
		}

		this.tickerRAf = requestAnimationFrame(() => this.ticker());
	}

	timelineChange(e) {
		this.pause();
		this.$video.currentTime = e.currentTarget.value * this.$video.duration / 100;
		
		let progress = (this.$video.currentTime / this.$video.duration) * 100;
		this.updateTimeValue();

		this.el.style.setProperty('--videoProgress',progress);

	}

	timelineChangeRelease(e) {
		this.play();
	}

	updateTimeValue() {
		let minutes = (Math.floor(this.$video.currentTime / 60)).toString();
		let seconds = (Math.floor(this.$video.currentTime)).toString();
		let milliseconds = (Math.floor((this.$video.currentTime - seconds)*60)).toString();

		this.$('minutes')[0].innerText = `${minutes.padStart(2,'0')}`;
		this.$('seconds')[0].innerText = `${seconds.padStart(2,'0')}`;
		this.$('milliseconds')[0].innerText = `${milliseconds.padStart(2,'0')}`;
		
	}

	// 
	// Utilities
	// 
	getFPS() {
		let lastMediaTime, lastFrameNum, fps;
		let fpsRounder = [];
		let frameNotSeeked = true;
		this.hasFPSValue = false;

		let fpsTicker = (useless, metadata) => {
			let mediaTimeDiff = Math.abs(metadata.mediaTime - lastMediaTime);
			let frameNumDiff = Math.abs(metadata.presentedFrames - lastFrameNum);
			let diff = mediaTimeDiff / frameNumDiff;

			if (
				diff &&
				diff < 1 &&
				frameNotSeeked &&
				fpsRounder.length < 50 &&
				this.$video.playbackRate === 1 &&
				!this.hasFPSValue
			) {
				fps = Math.round(1 / diff);
				this.$fpsValue.innerText = `${fps}FPS`;
				this.hasFPSValue = true;
			} else {
				frameNotSeeked = true;
				lastMediaTime = metadata.mediaTime;
				lastFrameNum = metadata.presentedFrames;

				this.$video.requestVideoFrameCallback(fpsTicker);

			}

		}

		if(typeof this.$video.requestVideoFrameCallback == 'function') {
			this.$video.requestVideoFrameCallback(fpsTicker);
		} else {
			this.$fpsValue.innerText = `30FPS`;
		}
		
		// this.bindSeeked = () => {
		// 	fpsRounder.pop();
		// 	frameNotSeeked = false;
		// };

		// this.$video.addEventListener(this.bindSeeked);
	}

	getFPSAverage(fpsRounder) {
		return fpsRounder.reduce((a, b) => a + b) / fpsRounder.length;
	}

	generateTimeline() {		
		// remove all images 
		this.$thumbnails.innerHTML = '';

		// Now, add the thumbnails from the back-extend
		this.thumbnails.forEach(thumbnail => {
			let img = new Image();
			img.src = thumbnail;
			this.$thumbnails.appendChild(img);
		});
	}

	destroy() {
		window.cancelAnimationFrame(this.tickerRAf);
	}
}
